import type {
  Asset,
  BonusType,
  LinkMode,
  Wheel,
  WheelPrizeType,
  WheelTheme,
} from '@longnecktech/splash-commons-fe';
import type { EnginePlayResponse, EngineWheelConfig } from './engine.models';

export function mapEngineWheel(
  config: EngineWheelConfig,
  theme: WheelTheme,
  labels: Record<string, string>,
): Wheel {
  if (!config || !Array.isArray(config.segments) || !config.segments.length) {
    throw new Error('The game configuration has no wheel segments.');
  }
  const ids = new Set<string>();
  const indices = new Set<number>();
  const segments = config.segments
    .map((segment) => {
      if (
        !segment.uuid ||
        ids.has(segment.uuid) ||
        !Number.isInteger(segment.index) ||
        segment.index < 0 ||
        indices.has(segment.index)
      ) {
        throw new Error('The wheel segments need unique IDs and positions.');
      }
      ids.add(segment.uuid);
      indices.add(segment.index);
      if (
        !segment.reward ||
        !Number.isFinite(segment.reward.quantity) ||
        segment.reward.quantity < 0 ||
        !segment.reward.displayName
      ) {
        throw new Error('A wheel segment is missing its reward details.');
      }
      const icon = {
        uuid: segment.uuid,
        url: segment.iconAsset?.url ?? '',
        name: segment.reward.displayName,
        label: segment.reward.displayName,
      } as Asset;
      return {
        uuid: segment.uuid,
        index: segment.index,
        text: segment.text,
        iconAsset: icon,
        backgroundColor:
          segment.backgroundColor ??
          (segment.index % 2 ? '#ee0000' : '#00b3cd'),
        chance: 0,
        assetLabel: segment.reward.displayName,
        prize: {
          type: 'CUSTOM' as WheelPrizeType,
          currency: segment.reward.unit,
          value: segment.reward.quantity,
          label: segment.reward.displayName,
          infoLabel: '',
          image: icon,
          bonusType: (segment.reward.type === 'FREE_SPINS'
            ? 'FREE_SPINS'
            : 'NON_DEPOSIT') as BonusType,
          slug: '',
        },
      };
    })
    .sort((a, b) => a.index - b.index);
  const translated = new Map(theme.labels.map((label) => [label.key, label]));
  for (const [key, value] of Object.entries(labels)) {
    if (typeof value === 'string')
      translated.set(key, { uuid: key, key, value, description: '', hint: '' });
  }
  return {
    uuid: config.uuid,
    name: config.name,
    startTs: config.startTs,
    endTs: config.endTs,
    paused: config.paused,
    ended: config.ended,
    theme: { ...theme, labels: [...translated.values()] },
    segments,
    hideCloseButton: false,
    termsLinkMode: 'HIDDEN' as LinkMode,
    gameRulesLinkMode: 'HIDDEN' as LinkMode,
    faqLinkMode: 'HIDDEN' as LinkMode,
    campaign: null,
    spinsPerDay: 0,
    delayBetweenSpins: 0,
    loginLink: null,
    featuredProducts: [],
    submissionCount: 0,
    termsAndConditions: null,
    rules: null,
    faq: null,
    settlementPostMessage: false,
    test: false,
  };
}

export function engineWinningSegment(
  game: Wheel,
  response: EnginePlayResponse,
) {
  const result = response.actions.filter(
    (action) => action.type === 'roll_result',
  );
  if (result.length !== 1) {
    throw new Error('The engine spin response has no unique landing position.');
  }
  const position = result[0].position;
  if (
    typeof position !== 'number' &&
    (typeof position !== 'string' || !/^\d+$/.test(position))
  ) {
    throw new Error('The engine returned an invalid landing position.');
  }
  const index = Number(position);
  const segment =
    Number.isSafeInteger(index) &&
    game.segments.find((item) => item.index === index);
  if (!segment)
    throw new Error(
      'The engine landing position does not match a wheel segment.',
    );
  if (
    response.actions.some(
      (action) => action.type === 'move_forward' || action.type === 'move_back',
    )
  ) {
    throw new Error('The engine returned board movement for a wheel spin.');
  }
  return segment;
}
